import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import omit from 'lodash/omit'
import { Alert, AlertTitle, alpha, Box, Stack, Typography } from '@mui/material'
import { Search } from '@mui/icons-material'
import { blue, red } from '@mui/material/colors'
import { formatTemperature } from '../utils/string'
import { AuroraAssetGroup, AuroraAwhpEvent, AuroraAwhpData, Sensor } from '../types'
import AuroraBlockCard from '../components/AuroraBlockCard'
import { useAuroraStack } from '../hooks/aurora'
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch'
import { getAuroraAwhpActiveEvents, getAuroraAwhpDataNow } from '../services/aurora'
import { getSensorNow } from '../services/deviceService'
import { useDispatch } from 'react-redux'
import { createErrorNotification } from '../redux/slices/notifications'

export interface AuroraAssetGroupStackProps {
  auroraAssetGroup: AuroraAssetGroup
  auroraAwhpData?: Record<string, AuroraAwhpData>
  auroraAwhpActiveEvents?: Record<string, AuroraAwhpEvent[]>
  sensors?: Record<string, Sensor>
}

export default function AuroraAssetGroupStack({
  auroraAssetGroup,
  auroraAwhpData: initialAuroraAwhpData,
  auroraAwhpActiveEvents: initialAuroraAwhpActiveEvents,
  sensors: initialSensors
}: AuroraAssetGroupStackProps) {
  const { t } = useTranslation('aurora')

  const dispatch = useDispatch()

  const { auroras, master, positions, masterPosition } = useAuroraStack(auroraAssetGroup)

  const [auroraAwhpData, setAuroraAwhpData] = useState<Record<string, AuroraAwhpData>>(
    initialAuroraAwhpData || {}
  )

  const [auroraAwhpActiveEvents, setAuroraAwhpActiveEvents] = useState<
    Record<string, AuroraAwhpEvent[]>
  >(initialAuroraAwhpActiveEvents || {})

  const [sensors, setSensors] = useState<Record<string, Sensor>>(initialSensors || {})

  const containerRef = useRef<HTMLDivElement>(null)

  const contentRef = useRef<HTMLDivElement>(null)

  const [fitScale, setFitScale] = useState(1)

  const minX = positions.length > 0 ? Math.min(...positions.map((position) => position.x)) : 0

  const maxX = positions.length > 0 ? Math.max(...positions.map((position) => position.x)) : 0

  const columns = useMemo(() => {
    const columns = []

    for (let column = minX; column <= maxX; column++) {
      columns.push(column)
    }

    return columns
  }, [minX, maxX])

  const minY = positions.length > 0 ? Math.min(...positions.map((position) => position.y)) : 0

  const maxY = positions.length > 0 ? Math.max(...positions.map((position) => position.y)) : 0

  const rows = useMemo(() => {
    const rows = []

    for (let row = maxY; row >= minY; row--) {
      rows.push(row)
    }

    return rows
  }, [minY, maxY])

  function findAssetByStackPosition(row: number, column: number) {
    // Get the asset IDs that are in the given row and column.
    const auroraIds = Object.keys(auroraAssetGroup.customData?.positions ?? {}).filter(
      (id) =>
        auroraAssetGroup.customData?.positions?.[id].y === row &&
        auroraAssetGroup.customData?.positions?.[id].x === column
    )

    return auroraAssetGroup.assets?.find(
      (asset) => auroraIds.includes(asset.auroraId) || auroraIds.includes(asset?.aurora?.id)
    )
  }

  async function loadAuroraAwhpData(controller: AbortController) {
    try {
      const auroraAwhpData: Record<string, AuroraAwhpData> = {}

      for (const aurora of auroras) {
        try {
          const auroraAwhpRecord = await getAuroraAwhpDataNow(aurora.id, {
            signal: controller.signal
          })
          auroraAwhpData[aurora.id] = auroraAwhpRecord
        } catch (_error) {
          dispatch(createErrorNotification('failedToLoadStatus'))
        }
      }

      setAuroraAwhpData(auroraAwhpData)
    } catch (error) {
      console.error(error)
    }
  }

  async function loadAuroraAwhpActiveEvents(controller: AbortController) {
    try {
      const auroraAwhpActiveEvents: Record<string, AuroraAwhpEvent[]> = {}

      for (const aurora of auroras) {
        try {
          const activeEvents = await getAuroraAwhpActiveEvents(aurora.id, {
            signal: controller.signal
          })
          auroraAwhpActiveEvents[aurora.id] = activeEvents
        } catch (error) {
          console.error(error)
        }
      }

      setAuroraAwhpActiveEvents(auroraAwhpActiveEvents)
    } catch (error) {
      console.error(error)
    }
  }

  async function loadSensors(controller: AbortController) {
    try {
      const sensors: Record<string, Sensor> = {}

      for (const aurora of auroras) {
        if (aurora.deviceId) {
          try {
            const sensor = await getSensorNow(aurora.deviceId, {
              signal: controller.signal
            })

            sensors[aurora.deviceId] = sensor
          } catch (error) {
            console.error(error)
          }
        }
      }

      setSensors(sensors)
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    if (initialAuroraAwhpData !== undefined) {
      setAuroraAwhpData(initialAuroraAwhpData)
    } else {
      const controller = new AbortController()

      loadAuroraAwhpData(controller)

      const ONE_MINUTE = 60 * 1000
      const interval = setInterval(() => {
        loadAuroraAwhpData(controller)
      }, ONE_MINUTE)

      return () => {
        clearInterval(interval)
        controller.abort()
      }
    }
  }, [auroras.length, initialAuroraAwhpData])

  useEffect(() => {
    if (initialAuroraAwhpActiveEvents !== undefined) {
      setAuroraAwhpActiveEvents(initialAuroraAwhpActiveEvents)
    } else {
      const controller = new AbortController()

      loadAuroraAwhpActiveEvents(controller)

      const ONE_MINUTE = 60 * 1000
      const interval = setInterval(() => {
        loadAuroraAwhpActiveEvents(controller)
      }, ONE_MINUTE)

      return () => {
        clearInterval(interval)
        controller.abort()
      }
    }
  }, [auroras.length, initialAuroraAwhpActiveEvents])

  useEffect(() => {
    if (initialSensors !== undefined) {
      setSensors(initialSensors)
    } else {
      const controller = new AbortController()

      loadSensors(controller)

      const ONE_MINUTE = 60 * 1000
      const interval = setInterval(() => {
        loadSensors(controller)
      }, ONE_MINUTE)

      return () => {
        clearInterval(interval)
        controller.abort()
      }
    }
  }, [auroras.length, initialSensors])

  useEffect(() => {
    const container = containerRef.current
    const content = contentRef.current

    if (!container || !content) {
      return
    }

    function updateFitScale() {
      const containerWidth = container?.clientWidth ?? 0
      const contentWidth = content?.scrollWidth ?? 0

      if (containerWidth > 0 && contentWidth > 0) {
        const scale = Math.round(Math.min(1, containerWidth / contentWidth) * 100) / 100

        setFitScale(scale)
      }
    }

    updateFitScale()

    const resizeObserver = new ResizeObserver(updateFitScale)
    resizeObserver.observe(container)
    resizeObserver.observe(content)

    return () => resizeObserver.disconnect()
  }, [rows, columns])

  function Cell({ row, column }: { row: number; column: number }) {
    const asset = findAssetByStackPosition(row, column)

    const awhpData =
      asset?.aurora?.id !== undefined && asset.aurora.id in auroraAwhpData
        ? auroraAwhpData[asset.aurora.id]
        : null

    const awhpActiveEvents =
      asset?.aurora?.id !== undefined && asset.aurora.id in auroraAwhpActiveEvents
        ? auroraAwhpActiveEvents[asset.aurora.id]
        : []

    const sensor =
      asset?.aurora?.deviceId !== undefined &&
      asset.aurora.deviceId !== null &&
      asset.aurora.deviceId in sensors
        ? sensors[asset.aurora.deviceId]
        : null

    return (
      <>
        {asset && asset?.aurora ? (
          <AuroraBlockCard
            aurora={{ ...asset.aurora, asset: omit(asset, 'aurora') }}
            auroraAwhpData={awhpData}
            auroraAwhpActiveEvents={awhpActiveEvents}
            sensor={sensor}
          />
        ) : (
          <Box
            sx={{
              width: 296,
              aspectRatio: '3 / 4',
              borderRadius: 2,
              flexShrink: { xs: 1, sm: 0 }
            }}
          />
        )}
      </>
    )
  }

  function PipeLine({
    label,
    temperature,
    color
  }: {
    label: string
    temperature: number | null
    color: string
  }) {
    const direction = masterPosition !== null && masterPosition.x === 0 ? 'left' : 'right'

    return (
      <Stack direction="row" alignItems="center" gap={0}>
        <Box
          sx={{
            backgroundImage:
              direction === 'right'
                ? `linear-gradient(to right, ${alpha(color, 1)}, ${alpha(color, 0.2)})`
                : `linear-gradient(to right, ${alpha(color, 0)}, ${alpha(color, 0.2)})`,
            width: direction === 'right' ? '48px' : '100%',
            flexShrink: direction === 'right' ? 0 : 1,
            height: 12
          }}
        />

        <Box
          sx={{
            display: 'flex',
            flexFlow: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            flexShrink: 0,
            flexGrow: 0,
            gap: 0,
            textAlign: 'center',
            py: 2,
            px: 4
          }}
        >
          {/*<Speed htmlColor={theme.palette.text.secondary} fontSize="small" sx={{ mb: -1 }} />*/}
          <Typography color="text.secondary">{label}</Typography>
          <Typography variant="h6">{formatTemperature(temperature)}</Typography>
        </Box>

        <Box
          sx={{
            backgroundImage:
              direction === 'right'
                ? `linear-gradient(to right, ${alpha(color, 0.2)}, ${alpha(color, 0)})`
                : `linear-gradient(to right, ${alpha(color, 0.2)}, ${alpha(color, 1)})`,
            width: direction === 'right' ? '100%' : '48px',
            flexShrink: direction === 'right' ? 1 : 0,
            height: 12
          }}
        />
      </Stack>
    )
  }

  function MasterBox() {
    if (master === null) {
      return <Box sx={{ width: 296, aspectRatio: '3 / 4', background: 'transparent' }} />
    }

    const direction = masterPosition !== null && masterPosition.x === 0 ? 'left' : 'right'

    const masterAwhpData =
      master?.id !== undefined && master?.id !== null && master.id in auroraAwhpData
        ? auroraAwhpData[master.id]
        : null

    const supplyTemperature = masterAwhpData ? masterAwhpData.temperature_secondary_supply : null

    const returnTemperature = masterAwhpData ? masterAwhpData.temperature_secondary_return : null

    const deltaTemperature =
      supplyTemperature !== null && returnTemperature !== null
        ? supplyTemperature - returnTemperature
        : null

    return (
      <Box
        sx={{
          width: 296,
          aspectRatio: '3 / 4',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-end',
          borderRadius: 2,
          flexGrow: 0,
          flexShrink: { xs: 1, sm: 0 },
          position: 'relative'
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            display: 'flex',
            flexFlow: 'column',
            justifyContent: 'flex-end',
            gap: 2,
            top: 0,
            width: '100%',
            bottom: 0,
            left: direction === 'right' ? '-8px' : 'auto',
            right: direction === 'right' ? 'auto' : '-8px'
          }}
        >
          <PipeLine
            label={t('supplyTemperature')}
            temperature={supplyTemperature}
            color={red[400]}
          />

          {/*<Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              alignSelf: direction === 'right' ? 'flex-start' : 'flex-end',
              flexFlow: 'row',
              width: '120px',
              // height: '48px',
              textAlign: 'center',
              gap: 1,
              px: '48px',
              transform: direction === 'right' ? 'rotate(-90deg)' : 'rotate(90deg)',
              position: 'relative',
              left: direction === 'right' ? '-32px' : 'auto',
              right: direction === 'right' ? 'auto' : '-32px',
              transformOrigin: 'center center'
            }}
          >
            {deltaTemperature !== null && (
              <>
                <Typography color="text.secondary">ΔT</Typography>
                <Typography>{formatTemperatureDelta(deltaTemperature)}</Typography>
              </>
            )}
          </Box>*/}

          <PipeLine
            label={t('returnTemperature')}
            temperature={returnTemperature}
            color={blue[400]}
          />
        </Box>
      </Box>
    )
  }

  if (auroraAssetGroup.assets?.length === 0) {
    return (
      <Alert severity="info" icon={<Search />}>
        <AlertTitle>{t('noUnits')}</AlertTitle>
      </Alert>
    )
  }

  return (
    <Box ref={containerRef} sx={{ width: '100%' }}>
      <TransformWrapper
        key={fitScale}
        initialScale={fitScale}
        minScale={Math.min(fitScale, 0.5)}
        maxScale={Math.max(fitScale, 1)}
        centerZoomedOut={false}
        disablePadding={true}
        limitToBounds={true}
        wheel={{ activationKeys: ['Control'] }}
        panning={{ lockAxisY: true }}
      >
        <TransformComponent
          contentStyle={{ width: 'max-content', minWidth: '100%' }}
          wrapperStyle={{ width: '100%', display: 'block' }}
        >
          <Stack
            ref={contentRef}
            sx={{
              flexDirection: 'column',
              flexWrap: 'nowrap',
              width: 'max-content',
              minWidth: '100%',
              gap: 2
            }}
          >
            {rows.map((row) => (
              <Stack
                key={row}
                sx={{
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItem: 'center',
                  flexWrap: 'nowrap',
                  width: '100%',
                  gap: 2
                }}
              >
                {master &&
                masterPosition !== null &&
                masterPosition.y === row &&
                masterPosition.x <= 0 ? (
                  <MasterBox />
                ) : (
                  <Box sx={{ width: 296, aspectRatio: '3 / 4', background: 'transparent' }} />
                )}

                {columns.map((column) => (
                  <Cell key={`${row}-${column}`} row={row} column={column} />
                ))}

                {master &&
                masterPosition !== null &&
                masterPosition.y === row &&
                masterPosition.x > 0 ? (
                  <MasterBox />
                ) : (
                  <Box sx={{ width: 296, aspectRatio: '3 / 4', background: 'transparent' }} />
                )}
              </Stack>
            ))}
          </Stack>
        </TransformComponent>
      </TransformWrapper>
    </Box>
  )
}
